import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Phone, MapPin, ArrowRight, Crosshair, Clock, Sparkles, MessageSquare } from 'lucide-react';
import { services, cities, builderBenefits, getCityPagesForService, getServicePagesForCity, getNearbyCityPages, getLocalProjects } from '@/data/localServiceData';

const LocalServicePage = ({ serviceSlug, citySlug }) => {
  const service = services[serviceSlug];
  const city = cities[citySlug];

  if (!service || !city) return null;

  const otherCityPages = getCityPagesForService(serviceSlug).filter(p => p.citySlug !== citySlug);
  const otherServicePages = getServicePagesForCity(citySlug).filter(p => p.serviceSlug !== serviceSlug);
  const nearbyCityPages = getNearbyCityPages(citySlug, serviceSlug);
  const localProjectsList = getLocalProjects(serviceSlug, citySlug);

  const pageTitle = `${service.name} in ${city.name}, TX | Comal Glass Company`;
  const pageDescription = `Custom ${service.name.toLowerCase()} installation in ${city.name}, ${city.state}. Serving ${city.county} with precision-measured, professionally installed glass solutions. Free quotes. Call (830) 359-9925.`;
  const canonicalUrl = `https://comalglass.com/${serviceSlug}-${citySlug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Comal Glass Company",
    "description": pageDescription,
    "url": canonicalUrl,
    "telephone": "(830) 359-9925",
    "email": "orders@comalglass.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "25191 State Hwy 46",
      "addressLocality": "Bulverde",
      "addressRegion": "TX",
      "postalCode": "78163",
      "addressCountry": "US"
    },
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": city.county
      }
    },
    "serviceType": service.name,
    "priceRange": "$$",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "45"
    }
  };

  // Add FAQ schema for priority cities
  const faqJsonLd = city.isPriority && city.localFAQ ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": city.localFAQ.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const benefitIcons = [Crosshair, Clock, Sparkles, MessageSquare];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" data-testid="local-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={service.heroImage}
            alt={pageTitle}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/50" />
        </div>
        <div className="container-custom relative z-10 text-white">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 backdrop-blur-sm">
              <MapPin className="h-3 w-3 mr-1" />
              Serving {city.name}, {city.state}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="local-page-h1">
              Custom <span className="text-gradient-cyan">{service.shortName}</span> in {city.name}, TX
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
              {service.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact" data-testid="local-hero-cta">{service.ctaText}</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                <a href="tel:8303599925" data-testid="local-hero-phone">
                  <Phone className="mr-2 h-5 w-5" />
                  (830) 359-9925
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Local Trust Section */}
      <section className="py-16 bg-gradient-subtle" data-testid="local-trust-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex p-3 bg-primary/10 rounded-xl flex-shrink-0 mt-1">
                <MapPin className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Your Local <span className="text-gradient-cyan">{city.name}</span> Glass Experts
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                  {city.trustParagraph}
                </p>
                <p className="text-sm text-muted-foreground mt-3">
                  We also serve {city.nearbyAreas}.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Priority City: Unique Local Content Section */}
      {city.isPriority && city.localProjects && (
        <section className="py-16 border-b" data-testid="local-unique-content">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              {/* Why [City] Homeowners Choose Us */}
              <div className="mb-12">
                <Badge className="mb-4 bg-cyan-500/10 text-cyan-600 border-cyan-500/30">Local Expertise</Badge>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  {city.localProjects.headline}
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
                  {city.localProjects.content}
                </p>
                
                {/* Featured Neighborhoods */}
                {city.localProjects.featuredNeighborhoods && (
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                      Neighborhoods & Areas We Serve in {city.name}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {city.localProjects.featuredNeighborhoods.map((neighborhood, idx) => (
                        <Badge key={idx} variant="outline" className="bg-white">{neighborhood}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Local FAQ */}
              {city.localFAQ && city.localFAQ.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-6">
                    Common Questions About {service.name} in {city.name}
                  </h3>
                  <div className="space-y-4">
                    {city.localFAQ.map((faq, idx) => (
                      <div key={idx} className="p-5 bg-white rounded-xl shadow-sm border">
                        <h4 className="font-semibold mb-2">{faq.question}</h4>
                        <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Driving Info */}
              {city.drivingInfo && (
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>{city.drivingInfo}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Featured Local Projects */}
      {localProjectsList && localProjectsList.length > 0 && (
        <section className="py-16 bg-gradient-subtle" data-testid="local-featured-project">
          <div className="container-custom">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4 bg-amber-500/10 text-amber-600 border-amber-500/30">
                  {localProjectsList.length > 1 ? 'Recent Projects' : 'Recent Project'}
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-bold">
                  Our Work in {city.name}
                </h2>
              </div>
              
              <div className="space-y-12">
                {localProjectsList.map((project, index) => (
                  <div 
                    key={index} 
                    className={`grid md:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
                  >
                    <div className={index % 2 === 1 ? 'md:order-2' : 'md:order-1'}>
                      <h3 className="text-xl font-bold mb-2">
                        {project.title}
                      </h3>
                      <p className="text-muted-foreground mb-3 leading-relaxed">
                        {project.description}
                      </p>
                      {project.caseStudy && (
                        <p className="text-sm text-muted-foreground italic border-l-2 border-primary/30 pl-4 mb-3">
                          {project.caseStudy}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        <MapPin className="inline h-3 w-3 mr-1" />
                        {city.name}, Texas
                      </p>
                    </div>
                    <div className={index % 2 === 1 ? 'md:order-1' : 'md:order-2'}>
                      <div className="rounded-xl overflow-hidden shadow-lg">
                        <img 
                          src={project.image} 
                          alt={`${project.title} - ${service.name} project in ${city.name}, Texas`}
                          className="w-full h-64 md:h-72 object-cover"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Link to="/portfolio" className="text-primary hover:underline font-medium text-sm">
                  View more projects in our portfolio →
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Core Service Features */}
      <section className="py-20" data-testid="local-features-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Why Choose Comal Glass</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {city.name}'s Premier <span className="text-gradient-cyan">{service.shortName}</span> Provider
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every project is custom-crafted with attention to detail that {city.name} homeowners and builders expect.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.features.map((feature, index) => (
              <Card key={index} className="border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                    <p className="text-sm">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white" data-testid="local-specs-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">Technical Details</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {service.shortName} <span className="text-gradient-cyan">Specifications</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {service.technicalSpecs.map((spec, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h3 className="text-primary font-semibold text-sm uppercase tracking-wide mb-2">{spec.label}</h3>
                <p className="text-gray-300">{spec.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Builder Benefits */}
      <section className="py-20" data-testid="local-builder-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4">For {city.name} Builders</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Built for <span className="text-gradient-cyan">Professionals</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                We partner with {city.name} and {city.county} builders to deliver on time, every time.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {builderBenefits.map((benefit, index) => {
                const Icon = benefitIcons[index];
                return (
                  <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold mb-1">{benefit.title}</h3>
                          <p className="text-sm text-muted-foreground">{benefit.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA + Map Section */}
      <section className="py-20 bg-gradient-subtle" data-testid="local-cta-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* CTA */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Ready for {service.shortName} in <span className="text-gradient-cyan">{city.name}?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                {service.ctaSubtext} Contact us today for a free consultation and quote for your {city.name} project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                  <Link to="/contact" data-testid="local-bottom-cta">Get Free Quote</Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <a href="tel:8303599925" data-testid="local-bottom-phone">
                    <Phone className="mr-2 h-5 w-5" />
                    (830) 359-9925
                  </a>
                </Button>
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                <p className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  25191 State Hwy 46, Bulverde, TX 78163
                </p>
                <p className="pl-6">Serving {city.name} and {city.nearbyAreas}</p>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-xl overflow-hidden shadow-lg">
              <iframe
                src={`https://www.google.com/maps?q=Comal+Glass+Company,+Bulverde,+TX&output=embed`}
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`Comal Glass Company - Serving ${city.name}`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nearby Areas - Cross-linking for SEO */}
      {nearbyCityPages.length > 0 && (
        <section className="py-12 border-b" data-testid="local-nearby-areas">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold mb-4">
                {service.name} in Nearby Areas
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Also serving these nearby Hill Country communities with expert {service.shortName.toLowerCase()} installation:
              </p>
              <div className="flex flex-wrap gap-2">
                {nearbyCityPages.map((page) => (
                  <Link
                    key={page.citySlug}
                    to={page.path}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/5 hover:bg-primary/10 rounded-full text-sm font-medium text-foreground hover:text-primary transition-colors border border-transparent hover:border-primary/20"
                  >
                    <MapPin className="h-3 w-3" />
                    {page.cityName}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Cities for Same Service */}
      <section className="py-16 bg-secondary text-secondary-foreground" data-testid="local-other-cities">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              {service.name} Across the <span className="text-gradient-cyan">Hill Country</span>
            </h2>
            <p className="text-muted-foreground">
              We proudly serve these communities with expert {service.shortName.toLowerCase()} installation.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {otherCityPages.map((page) => (
              <Button key={page.citySlug} variant="outline" size="sm" className="border-border" asChild>
                <Link to={page.path}>
                  {service.shortName} in {page.cityName}
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            ))}
            <Button variant="outline" size="sm" className="border-primary text-primary" asChild>
              <Link to={service.parentPath}>
                View All {service.name}
                <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Other Services in Same City */}
      {otherServicePages.length > 0 && (
        <section className="py-16" data-testid="local-other-services">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                More Glass Services in <span className="text-gradient-cyan">{city.name}</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {otherServicePages.map((page) => (
                <Card key={page.serviceSlug} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-5 pb-4 text-center">
                    <Link to={page.path} className="group">
                      <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">
                        {page.serviceName}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">{city.name}, TX</p>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LocalServicePage;
